import { BUSINESS_TIMEZONE, ORDER_TYPE_LABELS, formatLkr } from "./core";
import type { KitchenTicket, Order, Payment, PurchaseOrder, Settings, Supplier } from "./types";

// Code 128 bar/space widths for symbol values 0–105 plus the stop pattern (106).
const CODE128 = "212222 222122 222221 121223 121322 131222 122213 122312 132212 221213 221312 231212 112232 122132 122231 113222 123122 123221 223211 221132 221231 213212 223112 312131 311222 321122 321221 312212 322112 322211 212123 212321 232121 111323 131123 131321 112313 132113 132311 211313 231113 231311 112133 112331 132131 113123 113321 133121 313121 211331 231131 213113 213311 213131 311123 311321 331121 312113 312311 332111 314111 221411 431111 111224 111422 121124 121421 141122 141221 112214 112412 122114 122411 142112 142211 241211 221114 413111 241112 134111 111242 121142 121241 114212 124112 124211 411212 421112 421211 212141 214121 412121 111143 111341 131141 114113 114311 411113 411311 113141 114131 311141 411131 211412 211214 211232 2331112".split(" ");

/** Encodes printable ASCII as Code 128 (code set B). Returns symbol values including start, checksum and stop. */
export function code128Values(data: string): number[] {
  if (!/^[\x20-\x7e]{1,48}$/.test(data)) throw new Error("Code 128 supports 1–48 printable ASCII characters");
  const values = [104, ...Array.from(data, (char) => char.charCodeAt(0) - 32)];
  const checksum = values.reduce((sum, value, index) => sum + value * Math.max(index, 1), 0) % 103;
  return [...values, checksum, 106];
}
/** Alternating bar/space module widths, starting with a bar. */
export function code128Widths(data: string): number[] {
  return code128Values(data).flatMap((value) => Array.from(CODE128[value]!, Number));
}
export function code128Svg(data: string, height = 48, moduleWidth = 2): string {
  const quiet = 10 * moduleWidth;
  let x = quiet, bars = "";
  code128Widths(data).forEach((width, index) => {
    if (index % 2 === 0) bars += `<rect x="${x}" y="0" width="${width * moduleWidth}" height="${height}"/>`;
    x += width * moduleWidth;
  });
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${x + quiet} ${height}" width="${x + quiet}" height="${height}" shape-rendering="crispEdges" fill="#000">${bars}</svg>`;
}

export type PrintBlock =
  | { t: "text"; text: string; align?: "left" | "center" | "right"; bold?: boolean; size?: "normal" | "large" | "xlarge" }
  | { t: "row"; left: string; right: string; bold?: boolean; size?: "normal" | "large" }
  | { t: "rule" } | { t: "feed"; lines: number } | { t: "qr"; data: string } | { t: "barcode"; data: string }
  | PrintImage;
/** A picture prepared for both outputs: `src` for page printing, `bits` (1 bit per dot, rows padded to whole bytes, 1 = black) for ESC/POS. */
export type PrintImage = { t: "image"; src: string; widthPx: number; heightPx: number; bits: number[] };
export type PrintDocument = { widthMm: 58 | 80; title: string; blocks: PrintBlock[]; openDrawer: boolean };

export function formatColombo(iso: string): string {
  return new Intl.DateTimeFormat("en-GB", { timeZone: BUSINESS_TIMEZONE, day: "2-digit", month: "short", year: "numeric", hour: "numeric", minute: "2-digit", hour12: true }).format(new Date(iso));
}
const METHOD_LABELS = { cash: "Cash", card: "Card", bank_transfer: "Bank transfer" } as const;

export function orderStatusUrl(settings: Pick<Settings, "publicStatusBaseUrl">, order: Pick<Order, "publicToken">): string | undefined {
  return settings.publicStatusBaseUrl ? `${settings.publicStatusBaseUrl.replace(/\/+$/, "")}/#/status/${order.publicToken}` : undefined;
}

export function buildReceipt(order: Order, payments: readonly Payment[], settings: Settings, options: { widthMm: 58 | 80; reprint?: boolean; openDrawer?: boolean; logo?: PrintImage | undefined }): PrintDocument {
  const blocks: PrintBlock[] = options.logo ? [options.logo] : [];
  const text = (value: string, extra: Partial<Extract<PrintBlock, { t: "text" }>> = {}) => blocks.push({ t: "text", text: value, ...extra });
  const row = (left: string, right: string, extra: { bold?: boolean; size?: "normal" | "large" } = {}) => blocks.push({ t: "row", left, right, ...extra });
  text(settings.name, { align: "center", bold: true, size: "large" });
  for (const line of settings.addressLines) if (line) text(line, { align: "center" });
  if (settings.phone) text(`Tel: ${settings.phone}`, { align: "center" });
  if (options.reprint) text("*** REPRINT ***", { align: "center", bold: true });
  blocks.push({ t: "rule" });
  text(order.dailyNumber, { align: "center", bold: true, size: "xlarge" });
  text(`${ORDER_TYPE_LABELS[order.type]}${order.tableName ? ` - ${order.tableName}` : ""}`, { align: "center", bold: true });
  row("Date", formatColombo(order.createdAt));
  row("Served by", order.waiterName || order.createdByName);
  if (order.customerName || order.customerPhone) row("Customer", [order.customerName, order.customerPhone].filter(Boolean).join(" "));
  blocks.push({ t: "rule" });
  for (const line of order.lines) {
    if (line.voided) continue;
    const value = line.unitPriceMinor * line.quantity;
    row(`${line.quantity} x ${line.name}${line.sizeName ? ` (${line.sizeName})` : ""}`, line.complimentary ? "FREE" : formatLkr(value));
    for (const modifier of line.modifiers) text(`   + ${modifier.name}`);
    if (line.note) text(`   * ${line.note}`);
    if (line.complimentary) text(`   Complimentary (value ${formatLkr(value)})`);
  }
  blocks.push({ t: "rule" });
  const totals = order.totals;
  row("Subtotal", formatLkr(totals.grossMinor - totals.complimentaryMinor));
  for (const discount of order.discounts) row(discount.label, `-${formatLkr(discount.amountMinor)}`);
  if (totals.serviceChargeMinor > 0) row(`Service charge (${order.serviceChargeBps / 100}%)`, formatLkr(totals.serviceChargeMinor));
  if (totals.taxMinor > 0) row(`${settings.taxLabel} (${order.taxBps / 100}%)`, formatLkr(totals.taxMinor));
  row("TOTAL  LKR", formatLkr(totals.totalMinor), { bold: true, size: "large" });
  for (const payment of payments) {
    row(`Paid - ${METHOD_LABELS[payment.method]}`, formatLkr(payment.method === "cash" ? payment.tenderedMinor : payment.amountMinor));
    if (payment.changeMinor > 0) row("Change", formatLkr(payment.changeMinor));
  }
  if (order.refundedMinor > 0) row("Refunded", `-${formatLkr(order.refundedMinor)}`);
  const due = totals.totalMinor - order.paidMinor;
  if (due > 0 && order.status !== "voided") row("BALANCE DUE", formatLkr(due), { bold: true });
  if (order.status === "voided") text("*** CANCELLED ***", { align: "center", bold: true });
  if (totals.complimentaryMinor > 0 && totals.totalMinor === 0) text("COMPLIMENTARY - NO CHARGE", { align: "center", bold: true });
  blocks.push({ t: "rule" });
  text(`Ref ${order.ref}`, { align: "center" });
  if (settings.receiptFooter) text(settings.receiptFooter, { align: "center" });
  return { widthMm: options.widthMm, title: `Receipt ${order.dailyNumber}`, blocks, openDrawer: options.openDrawer ?? false };
}

const quantity = (milli: number) => (milli / 1000).toLocaleString("en-LK", { maximumFractionDigits: 3 });

/** A purchase order on the POS receipt printer, to hand to the supplier's driver or keep with the delivery. */
export function buildPurchaseOrder(order: PurchaseOrder, settings: Settings, widthMm: 58 | 80): PrintDocument {
  const blocks: PrintBlock[] = [
    { t: "text", text: settings.name, align: "center", bold: true, size: "large" },
    ...settings.addressLines.filter(Boolean).map((line): PrintBlock => ({ t: "text", text: line, align: "center" })),
    ...(settings.phone ? [{ t: "text", text: `Tel: ${settings.phone}`, align: "center" } as PrintBlock] : []),
    { t: "rule" },
    { t: "text", text: "PURCHASE ORDER", align: "center", bold: true, size: "large" },
    { t: "text", text: order.number, align: "center", bold: true },
    { t: "row", left: "Supplier", right: order.supplierName, bold: true },
    { t: "row", left: "Date", right: formatColombo(order.createdAt) },
    { t: "rule" }
  ];
  for (const line of order.lines) {
    blocks.push({ t: "row", left: line.name, right: `${quantity(line.qtyMilli)} ${line.unit}`, bold: true });
    if (line.unitCostMinor) blocks.push({ t: "row", left: `  @ ${formatLkr(line.unitCostMinor)} / ${line.unit}`, right: formatLkr(Math.round(line.qtyMilli * line.unitCostMinor / 1000)) });
  }
  blocks.push({ t: "rule" });
  if (order.totalMinor) blocks.push({ t: "row", left: "Estimated total", right: formatLkr(order.totalMinor), bold: true });
  if (order.note) blocks.push({ t: "text", text: `Note: ${order.note}` });
  blocks.push({ t: "text", text: `Ordered by ${order.createdByName}` }, { t: "feed", lines: 3 });
  return { widthMm, title: `${order.number} ${order.supplierName}`, blocks, openDrawer: false };
}

/** The order as a WhatsApp message to the supplier. */
export function purchaseOrderText(order: PurchaseOrder, settings: Pick<Settings, "name" | "phone">): string {
  return [
    `*Purchase order ${order.number}* - ${settings.name}`, `Date: ${formatColombo(order.createdAt)}`, "",
    ...order.lines.map((line) => `- ${line.name}: ${quantity(line.qtyMilli)} ${line.unit}`),
    ...(order.note ? ["", `Note: ${order.note}`] : []), "", `Please confirm. ${settings.phone ? `Tel: ${settings.phone}` : ""}`.trim()
  ].join("\n");
}

/** wa.me link for a Sri Lankan number ("077 123 4567" or "+94 77 ..."), or null when the number cannot be read. */
export function whatsappLink(phone: string, text: string): string | null {
  let digits = phone.replace(/\D/g, "");
  if (digits.startsWith("0")) digits = `94${digits.slice(1)}`;
  if (digits.length === 9) digits = `94${digits}`;
  return /^\d{10,15}$/.test(digits) ? `https://wa.me/${digits}?text=${encodeURIComponent(text)}` : null;
}
/** The number orders go to: the supplier's WhatsApp number, or the phone number. */
export const supplierWhatsapp = (supplier: Pick<Supplier, "phone" | "whatsapp"> | undefined) => supplier?.whatsapp || supplier?.phone || "";

export function buildKot(ticket: KitchenTicket, widthMm: 58 | 80, reprint = false): PrintDocument {
  const blocks: PrintBlock[] = [
    { t: "text", text: ticket.incremental ? "KITCHEN ORDER - ADDITIONAL" : "KITCHEN ORDER", align: "center", bold: true },
    { t: "text", text: ticket.dailyNumber, align: "center", bold: true, size: "xlarge" },
    { t: "text", text: `${ORDER_TYPE_LABELS[ticket.type]}${ticket.tableName ? ` - ${ticket.tableName}` : ""}`, align: "center", bold: true, size: "large" }
  ];
  if (reprint) blocks.push({ t: "text", text: "*** REPRINT ***", align: "center", bold: true });
  if (ticket.type === "call" && ticket.customerPhone) blocks.push({ t: "text", text: `Call: ${ticket.customerPhone}`, align: "center" });
  blocks.push({ t: "row", left: formatColombo(ticket.createdAt), right: ticket.incremental ? `Ticket #${ticket.batch}` : "" }, { t: "row", left: "By", right: ticket.createdByName }, { t: "rule" });
  for (const line of ticket.lines) {
    blocks.push({ t: "text", text: `${line.quantity} x ${line.name}${line.sizeName ? ` (${line.sizeName})` : ""}`, bold: true, size: "large" });
    for (const modifier of line.modifiers) blocks.push({ t: "text", text: `   + ${modifier}` });
    if (line.note) blocks.push({ t: "text", text: `   * ${line.note}`, bold: true });
  }
  if (ticket.note) blocks.push({ t: "rule" }, { t: "text", text: `NOTE: ${ticket.note}`, bold: true });
  blocks.push({ t: "rule" });
  return { widthMm, title: `KOT ${ticket.dailyNumber}`, blocks, openDrawer: false };
}

const ASCII_FALLBACK: Record<string, string> = { "·": "-", "–": "-", "—": "-", "‘": "'", "’": "'", "“": '"', "”": '"', "×": "x", "…": "..." };
const ascii = (value: string) => Array.from(value, (char) => char.charCodeAt(0) < 0x7f && char.charCodeAt(0) >= 0x20 ? char : ASCII_FALLBACK[char] ?? "?").join("");
function wrap(value: string, width: number): string[] {
  const lines: string[] = [];
  let current = "";
  for (const word of ascii(value).split(" ")) {
    let rest = word;
    while (rest.length > width) { if (current) { lines.push(current); current = ""; } lines.push(rest.slice(0, width)); rest = rest.slice(width); }
    if (!current) current = rest;
    else if (current.length + 1 + rest.length <= width) current += ` ${rest}`;
    else { lines.push(current); current = rest; }
  }
  lines.push(current);
  return lines;
}

/** Renders a document as ESC/POS bytes (Font A: 32 columns on 58 mm, 48 on 80 mm). */
export function encodeEscPos(document: PrintDocument): Uint8Array {
  const columns = document.widthMm === 58 ? 32 : 48;
  const bytes: number[] = [0x1b, 0x40];
  const put = (...values: number[]) => bytes.push(...values);
  const line = (value: string) => { for (const char of value) put(char.charCodeAt(0)); put(0x0a); };
  const style = (bold: boolean | undefined, size: string | undefined) => {
    put(0x1b, 0x45, bold ? 1 : 0);
    put(0x1d, 0x21, size === "xlarge" ? 0x11 : size === "large" ? 0x01 : 0x00);
    return size === "xlarge" ? Math.floor(columns / 2) : columns;
  };
  if (document.openDrawer) put(0x1b, 0x70, 0x00, 0x19, 0xfa);
  for (const block of document.blocks) {
    if (block.t === "text") {
      put(0x1b, 0x61, block.align === "center" ? 1 : block.align === "right" ? 2 : 0);
      const width = style(block.bold, block.size);
      wrap(block.text, width).forEach(line);
    } else if (block.t === "row") {
      put(0x1b, 0x61, 0);
      const width = style(block.bold, block.size);
      const right = ascii(block.right);
      const left = wrap(block.left, Math.max(8, width - right.length - 1));
      left.forEach((value, index) => line(index === left.length - 1 ? value.padEnd(width - right.length) + right : value));
    } else if (block.t === "rule") { put(0x1b, 0x61, 0); style(false, "normal"); line("-".repeat(columns)); }
    else if (block.t === "feed") put(0x1b, 0x64, block.lines);
    else if (block.t === "image") {
      // GS v 0: raster bit image, one byte per 8 dots.
      const rowBytes = Math.ceil(block.widthPx / 8);
      put(0x1b, 0x61, 1, 0x1d, 0x76, 0x30, 0, rowBytes & 0xff, rowBytes >> 8, block.heightPx & 0xff, block.heightPx >> 8, ...block.bits, 0x0a);
    } else if (block.t === "qr") {
      const data = Array.from(ascii(block.data), (char) => char.charCodeAt(0));
      put(0x1b, 0x61, 1, 0x1d, 0x28, 0x6b, 4, 0, 49, 65, 50, 0, 0x1d, 0x28, 0x6b, 3, 0, 49, 67, document.widthMm === 58 ? 5 : 6, 0x1d, 0x28, 0x6b, 3, 0, 49, 69, 49);
      put(0x1d, 0x28, 0x6b, (data.length + 3) & 0xff, (data.length + 3) >> 8, 49, 80, 48, ...data, 0x1d, 0x28, 0x6b, 3, 0, 49, 81, 48, 0x0a);
    } else {
      const data = Array.from(`{B${ascii(block.data)}`, (char) => char.charCodeAt(0));
      put(0x1b, 0x61, 1, 0x1d, 0x68, 64, 0x1d, 0x77, 2, 0x1d, 0x48, 0, 0x1d, 0x6b, 73, data.length, ...data, 0x0a);
    }
  }
  put(0x1b, 0x61, 0, 0x1b, 0x45, 0, 0x1d, 0x21, 0, 0x1b, 0x64, 4, 0x1d, 0x56, 66, 0);
  return Uint8Array.from(bytes);
}
